/**
 * Whether an aircraft casts a real shadow (05 §8.2). Reported in play: "the draw distance on the
 * aircraft looks a bit weird with shadows warping in."
 *
 * The sun's shadow frustum is a 68 m box that follows the camera's focus (`WorldLights`). An Osprey
 * on its approach sits about a hundred metres out and forty up, so it spends the whole run OUTSIDE
 * that box and then crosses into it - and a shadow that snaps into existence and stretches as it
 * comes is worse than no shadow at all. A real aircraft at height casts nothing a man on the ground
 * would notice anyway; what he sees is the disc under the canopy, which the insertion view draws
 * deliberately and which cannot warp, being flat on the ground.
 *
 * So: a shadow while it is low enough for one to mean something, and none while it is high.
 */

import type { Object3D } from 'three';

/**
 * Metres above the ground past which an aircraft stops casting. Comfortably above the Osprey's
 * 7 m hover - where it IS over the ranger, and so inside the frustum - and well under the altitudes
 * the approach and the insertion run fly at.
 */
export const SHADOW_CEILING = 12;

/** Set `castShadow` on every mesh of a model, in one pass. */
export function setCastShadow(root: Object3D, on: boolean): void {
  root.traverse((node) => {
    // Only meshes carry it; a Group's flag is inherited by nothing.
    if ((node as { isMesh?: boolean }).isMesh === true) node.castShadow = on;
  });
}

/** True while this height above the ground is worth casting from. Pure. */
export const castsFrom = (heightAboveGround: number): boolean => heightAboveGround <= SHADOW_CEILING;
